import {
  Avatar,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactComponent as HandShakeIcon } from 'assets/handshake.svg';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';

type DealInMotionProps = {
  setTabLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  setValue?: React.Dispatch<React.SetStateAction<number>>;
  path?: string;
  channelID?: string;
  rows?: DealInMotionRow[];
};

type DealInMotionRow = {
  id: string;
  dealName: string;
  client: string;
  clientLogo?: string | null;
  value: number;
  pipelineType: string;
  closeDate: string;
  owner: string;
  stage: string;
};

type CloseDateState = 'past' | 'near' | 'normal';

const getCloseDateState = (closeDate: string): CloseDateState => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const closing = new Date(closeDate);
  closing.setHours(0, 0, 0, 0);

  const diffInDays = Math.ceil((closing.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) {
    return 'past';
  }

  if (diffInDays <= 7) {
    return 'near';
  }

  return 'normal';
};

const getSeverityColor = (closeDate: string) => {
  const state = getCloseDateState(closeDate);

  if (state === 'past') {
    return zeplinColor.Error;
  }

  if (state === 'near') {
    return '#F59E0B';
  }

  return zeplinColor.Success;
};

const getStageColor = (stage: string) => {
  switch (stage.trim().toLowerCase()) {
    case 'exploring':
    case 'identified':
      return zeplinColor.Brand1;
    case 'qualified':
    case 'pitching':
      return zeplinColor.Blue;
    case 'negotiating':
    case 'contracting':
      return zeplinColor.AlertInfo;
    case 'active':
      return zeplinColor.Success;

    default:
      return zeplinColor.Success;
  }
};

const getHighlightColor = (state: CloseDateState) => {
  if (state === 'past') {
    return {
      background: '#FDECEC',
      border: zeplinColor.Error,
    };
  }

  if (state === 'near') {
    return {
      background: '#FFF7E6',
      border: '#F59E0B',
    };
  }

  return {
    background: 'transparent',
    border: 'transparent',
  };
};

const formatValue = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((item) => item[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const DealInMotion = ({ setTabLoading, rows }: DealInMotionProps) => {
  const navigate = useNavigate();
  const [tableRows, setTableRows] = React.useState<DealInMotionRow[]>([]);
  const [loading, setLoading] = React.useState(false);

  const getDealsInMotion = React.useCallback(async () => {
    try {
      setLoading(true);
      setTabLoading(true);

      const queryParams: any = {
        page: 1,
        limit: 20,
        sort: 'closeDate:asc',
        'stageName!=': ['Closed Lost', 'Closed Won'],
      };

      const data = await DealsService.getDealsListView(queryParams);
      const source = Array.isArray(data) ? data : data?.deals || data?.data || [];

      const mapped = source
        .filter((deal: any) => {
          const stage = String(deal?.stageName || deal?.dealStageName || deal?.stage || '')
            .trim()
            .toLowerCase();
          return stage !== 'closed lost' && stage !== 'closed won';
        })
        .map((deal: any, index: number) => {
          // --- deal name ---
          const rawDealName = deal?.name || 'N/A';

          // --- client ---
          const clientName = deal?.clientName || 'N/A';

          // --- pipeline type ---
          let pipelineType = deal?.dealType?.dealTypeKey || 'N/A';

          // --- value ---
          const rawValue = Number(deal?.amount ?? 0);
          const value = Number.isFinite(rawValue) ? rawValue : 0;

          // --- close date ---
          const closeDate = deal?.closeDate || deal?.termEnd || 'N/A';

          // --- owner ---
          const owner = deal?.owner?.fullName || 'N/A';

          // --- stage ---
          const stage = deal?.stageName || 'N/A';

          return {
            id: deal?.dealId,
            dealName: rawDealName,
            client: clientName,
            clientLogo: deal?.profilePicture || deal?.clientLogo || deal?.avatar || null,
            value,
            pipelineType: pipelineType || 'N/A',
            closeDate,
            owner,
            stage,
          } as DealInMotionRow;
        });

      setTableRows(mapped);
    } catch (error) {
      setTableRows([]);
    } finally {
      setLoading(false);
      setTabLoading(false);
    }
  }, [setTabLoading]);

  React.useEffect(() => {
    if (rows?.length) {
      setTableRows(rows);
      setTabLoading(false);
      return;
    }
    getDealsInMotion();
  }, [rows, getDealsInMotion, setTabLoading]);

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: `1px solid ${zeplinColor.Neutral5}`,
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
        minHeight: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: '16px',
          py: '14px',
          borderBottom: `1px solid ${zeplinColor.Neutral5}`,
        }}
      >
        <Typography variant='responsiveHeading3'>Deals In Motion</Typography>
        <Typography
          variant='responsiveButtonSmall'
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => navigate('/deals')}
        >
          See All
        </Typography>
      </Box>

      <TableContainer
        sx={{
          maxHeight: { xs: 'calc(100vh - 490px)', xl: 'calc(100vh - 650px)' },
          overflowY: 'auto',
          overflowX: 'auto',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                  backgroundColor: zeplinColor.Neutral3,
                  py: '12px',
                  color: zeplinColor.BasicBlack,
                },
              }}
            >
              <TableCell>
                <Typography variant='responsiveBody3'>Deal Name</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Client</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Value</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Pipeline Type</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Stage</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Close Date</Typography>
              </TableCell>
              <TableCell>
                <Typography variant='responsiveBody3'>Owner</Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align='center' sx={{ py: '24px' }}>
                  <Typography variant='responsiveBody2'>Loading deals...</Typography>
                </TableCell>
              </TableRow>
            ) : tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ border: 'none' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',

                      width: '100%',
                      gap: { xs: '20px', xl: '32px' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: { xs: '6px', xl: '12px' },
                      }}
                    >
                      <HandShakeIcon style={{ width: 48, height: 48 }} />
                      <Typography
                        variant='responsiveHeading3'
                        color={zeplinColor.BasicBlack}
                        sx={{ marginTop: { xs: '22px', xl: '24px' } }}
                      >
                        No Deals Yet
                      </Typography>
                      <Typography
                        variant='responsiveBody3'
                        color={zeplinColor.Neutral8}
                        sx={{
                          width: '70%',
                          textAlign: 'center',
                          marginTop: { xs: '10px', xl: '12px' },
                        }}
                      >
                        Your deals list is currently empty. Create your first deal to start tracking
                        your progress.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '14px',
                        height: 56,
                        backgroundColor: zeplinColor.Brand1,
                        padding: '14px 16px',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 },
                      }}
                      onClick={() => navigate('/deals')}
                    >
                      <img src='/assets/add-white.svg' alt='add' width={20} height={20} />
                      <Typography variant='responsiveButtonSmall' color={zeplinColor.Neutral1}>
                        Create Deals
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              tableRows.map((row, ind) => {
                const rowState = getCloseDateState(row.closeDate);
                const isHighlighted = rowState !== 'normal';
                const prevRowState =
                  ind > 0 ? getCloseDateState(tableRows[ind - 1].closeDate) : 'normal';
                const nextRowState =
                  ind < tableRows.length - 1
                    ? getCloseDateState(tableRows[ind + 1].closeDate)
                    : 'normal';

                const shouldShowTopBorder = isHighlighted && prevRowState !== rowState;
                const shouldShowBottomBorder = isHighlighted && nextRowState !== rowState;
                const highlightColors = getHighlightColor(rowState);

                const highlightedCellSx = isHighlighted
                  ? {
                      backgroundColor: highlightColors.background,
                      ...(shouldShowTopBorder && {
                        borderTop: `1px solid ${highlightColors.border}`,
                      }),
                      ...(shouldShowBottomBorder && {
                        borderBottom: `1px solid ${highlightColors.border}`,
                      }),
                    }
                  : undefined;

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: isHighlighted ? highlightColors.background : 'transparent',
                      '&.MuiTableRow-hover,&.MuiTableRow-hover:hover': {
                        backgroundColor: isHighlighted ? highlightColors.background : undefined,
                      },
                    }}
                  >
                    <TableCell sx={highlightedCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: getSeverityColor(row.closeDate),
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant='responsiveBody2'>{row.dealName}</Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar
                          src={row.clientLogo || undefined}
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '12px',
                            backgroundColor: '#E2E8F0',
                            color: zeplinColor.BasicBlack,
                          }}
                        >
                          {getInitials(row.client)}
                        </Avatar>
                        <Typography variant='responsiveBody2'>{row.client}</Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Typography variant='responsiveBody2'>{formatValue(row.value)}</Typography>
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Chip
                        label={row.pipelineType}
                        size='small'
                        sx={{
                          height: '28px',
                          borderRadius: '999px',
                          fontWeight: 500,
                          backgroundColor: zeplinColor.BasicBlack,
                          color: zeplinColor.Neutral1,
                        }}
                      />
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Chip
                        label={row.stage}
                        size='small'
                        sx={{
                          height: '28px',
                          borderRadius: '999px',
                          fontWeight: 500,
                          backgroundColor: getStageColor(row.stage),
                          color: zeplinColor.Neutral1,
                        }}
                      />
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Typography variant='responsiveBody2'>
                        {Utilities.formatDate(row.closeDate)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={highlightedCellSx}>
                      <Typography variant='responsiveBody2'>{row.owner}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DealInMotion;
